import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { useFeatureGate } from '../components/FeatureGate';
import { toast } from 'sonner';
import { FaCheckCircle, FaSpinner, FaTimesCircle } from 'react-icons/fa';
import { BrandLogo } from '../components/BrandLogo';

const API = '/api';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { refreshSubscription } = useFeatureGate();
  
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempt = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true
      });

      const data = response.data;

      if (data.payment_status === 'paid' || data.status === 'completed') {
        setPaymentInfo(data);
        setStatus('success');
        // Refresh subscription state so features are immediately available
        if (refreshSubscription) {
          refreshSubscription();
        }
        // Show success toast
        toast.success('🎉 Subscription Updated!', {
          description: 'Your new features are now available.',
          duration: 5000,
        });
        return;
      } else if (data.status === 'expired' || data.status === 'failed') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setPollCount(attempt + 1);
      setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);

    } catch (err) {
      console.error('Error checking payment status:', err);
      // If unauthorized, the user needs to log in again
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again to verify your payment.');
        setStatus('error');
      } else {
        setError(err.response?.data?.detail || 'Failed to verify payment');
        setStatus('error');
      }
    }
  }, [sessionId, refreshSubscription]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!sessionId) {
      setError('No session ID found');
      setStatus('error');
      return;
    }

    // Start polling
    pollPaymentStatus(0);
  }, [sessionId, authLoading, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/dashboard" className="inline-flex items-center space-x-2 text-emerald-600 hover:text-emerald-700">
            <BrandLogo className="w-9 h-9" testId="payment-success-logo" />
            <span className="text-xl font-bold">Legacy Forge Family</span>
          </Link>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          {status === 'checking' && (
            <>
              <FaSpinner className="text-6xl text-emerald-600 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment</h1>
              <p className="text-gray-600">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h1>
              <p className="text-gray-600 mb-4">Thank you for your payment.</p>
              
              {paymentInfo && (
                <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-600">Amount: <span className="font-bold text-gray-800">${paymentInfo.amount?.toFixed(2)}</span></p>
                  {paymentInfo.receipt_number && (
                    <p className="text-sm text-gray-600">Receipt: <span className="font-bold text-gray-800">{paymentInfo.receipt_number}</span></p>
                  )}
                </div>
              )}

              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
              >
                Return to Dashboard
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h1>
              <p className="text-gray-600 mb-6">Your payment could not be processed. Please try again.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
              >
                Return to Dashboard
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <FaSpinner className="text-6xl text-yellow-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Verification Timeout</h1>
              <p className="text-gray-600 mb-6">Payment verification is taking longer than expected. Your payment may still be processing.</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
              >
                Return to Dashboard
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
              <p className="text-gray-600 mb-6">{error || 'An error occurred while verifying your payment.'}</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
              >
                Return to Dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
